import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from '../api/axiosInstance';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

function formatLocalDateTime(input) {
  const dt = new Date(input);
  const offset = dt.getTimezoneOffset();
  const localDate = new Date(dt.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
}

function BloodPressureHistory() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [userName, setUserName] = useState("訪客");
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() =>
    new Date().toISOString().split("T")[0]
  );
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const fetchRecords = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `/api/BloodPressure?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setRecords(res.data);
      setError("");
    } catch {
      setError("查詢失敗，請重新登入或稍後再試。");
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setEditData({
      recordedAt: formatLocalDateTime(record.recordedAt),
      systolic: record.systolic,
      diastolic: record.diastolic,
      heartRate: record.heartRate || "",
      notes: record.notes || "",
    });
  };

  const handleSave = async (id) => {
    if (!window.confirm("確定要儲存修改？")) return;
    try {
      const token = localStorage.getItem("token");

      const payload = {
        ...editData,
        heartRate:
          editData.heartRate === "" || editData.heartRate === undefined
            ? null
            : parseInt(editData.heartRate),
        systolic: parseInt(editData.systolic),
        diastolic: parseInt(editData.diastolic),
      };

      await axios.put(`/api/BloodPressure/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditingId(null);
      fetchRecords();
    } catch {
      alert("更新失敗！");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("確定要刪除這筆紀錄？")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/BloodPressure/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchRecords();
    } catch {
      alert("刪除失敗！");
    }
  };

  useEffect(() => {
    document.title = "歷史血壓紀錄";
    fetchRecords();

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user?.name) {
        setUserName(user.name);
      }
    } catch (err) {
      console.error("讀取 user 發生錯誤", err);
    }
  }, []);

  const chartData = records.map((r) => ({
    recordedAt: r.recordedAt?.slice(0, 10),
    systolic: r.systolic,
    diastolic: r.diastolic,
    heartRate: r.heartRate,
  }));

  return (
    <div className="container mt-4">
      <h2 className="mb-4">血壓紀錄 - {userName}</h2>

      <div className="mb-3">
        <button className="btn btn-success me-2" onClick={() => navigate("/blood-pressure/new")}>
          新增血壓記錄
        </button>
        <button className="btn btn-outline-secondary" onClick={() => navigate("/dashboard")}>
          回到目錄
        </button>
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label">起始日期</label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label">結束日期</label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={fetchRecords}>
            查詢
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* ✅ 新增折線圖 */}
      {records.length > 0 && (
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            {/* <LineChart data={records}> */}
            <LineChart data={[...records].reverse()}>
              <XAxis dataKey="recordedAt" />
              <YAxis yAxisId="left" domain={["auto", "auto"]} />
              <YAxis yAxisId="right" orientation="right" domain={["auto", "auto"]} />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="systolic"
                stroke="#e74c3c"
                name="收縮壓"
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="diastolic"
                stroke="#f39c12"
                name="舒張壓"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="heartRate"
                stroke="#3498db"
                name="心跳"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th>日期時間</th>
              <th>收縮壓</th>
              <th>舒張壓</th>
              <th>心跳</th>
              <th>備註</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">尚無資料</td>
              </tr>
            ) : (
              records.map((r) =>
                editingId === r.id ? (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={editData.recordedAt}
                        onChange={(e) => setEditData({ ...editData, recordedAt: e.target.value })}
                      />
                    </td>
                    <td><input className="form-control" value={editData.systolic} onChange={(e) => setEditData({ ...editData, systolic: e.target.value })} /></td>
                    <td><input className="form-control" value={editData.diastolic} onChange={(e) => setEditData({ ...editData, diastolic: e.target.value })} /></td>
                    <td><input className="form-control" value={editData.heartRate} onChange={(e) => setEditData({ ...editData, heartRate: e.target.value })} /></td>
                    <td><input className="form-control" value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} /></td>
                    <td>
                      <button className="btn btn-success btn-sm me-1" onClick={() => handleSave(r.id)}>儲存</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>取消</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id}>
                    <td>{new Date(r.recordedAt).toLocaleString("zh-TW", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}</td>
                    <td>{r.systolic}</td>
                    <td>{r.diastolic}</td>
                    <td>{r.heartRate}</td>
                    <td>{r.notes}</td>
                    <td>
                      {r.source === r.userId && (
                        <>
                          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => handleEdit(r)}>編輯</button>
                          <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(r.id)}>刪除</button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BloodPressureHistory;
